type Handler<T> = (data: T) => void | Promise<void>;

/**
 * Simple typed publish/subscribe event bus.
 *
 * `publish` awaits all subscribers, so async handlers (e.g. typewriter animations)
 * are naturally sequenced when the caller uses `await pubsub.publish(...)`.
 */
export class PubSub<Events extends Record<string, any>> {
    private handlers = new Map<keyof Events, Handler<any>[]>();

    /**
     * Subscribe to an event. Returns an unsubscribe function.
     */
    subscribe<K extends keyof Events>(event: K, handler: Handler<Events[K]>): () => void {
        if (!this.handlers.has(event)) {
            this.handlers.set(event, []);
        }
        const list = this.handlers.get(event)!;
        list.push(handler);
        return () => {
            const idx = list.indexOf(handler);
            if (idx !== -1) list.splice(idx, 1);
        };
    }

    /**
     * Publish an event and await all subscribers.
     * For void events, the data argument is omitted.
     */
    async publish<K extends keyof Events>(
        event: K,
        ...args: Events[K] extends void ? [] : [Events[K]]
    ): Promise<void> {
        const data = args[0] as Events[K];
        const list = this.handlers.get(event);
        if (list) {
            await Promise.all(list.map(h => h(data)));
        }
    }
}
